/**
 * Delivery-job printout data.
 *
 * The card contents do NOT come from the fulfillment-job row. The job is
 * only the skeleton; the items come from the order snapshot via
 * `delivery/app/packaging.ts`, and the two read faces list line items
 * DIFFERENTLY on purpose — the job face is a raw-data view (the whole order
 * for a job with a 配送单清单, ADR-0046 decision 6; only the dated lines for a
 * job whose 配送单清单 is NULL), the packing face is the packing truth and
 * keeps undated items, because a packing sheet must never silently lose a
 * physical thing (#1694, #1706, #1716). So the sheet is fetched from
 * `/api/packaging` with the ids of the jobs being printed, rather than
 * rendered from the rows the list already holds.
 *
 * A plain function rather than a hook: it is called from the print job's
 * `buildSheet`, which runs inside an event handler and not during render.
 */

import { http } from '@lib/api';

export interface PackagingItem {
  line_item_id: string;
  title: string;
  sku: string | null;
  quantity: number;
  properties: Array<{ name: string; value: string }>;
  product_type: string | null;
  combo: {
    name: string;
    dishes: string[];
    gift: string | null;
  } | null;
  /**
   * The printed job has a readable `line_items` and no live job of the order
   * holds this line; listed so nothing is missed. Always false on a job that
   * prints by today's rule (its `line_items` is NULL or unreadable, or the
   * order's `created_at` cannot be read).
   */
  unallocated: boolean;
}

export interface PackagingCustomerEntry {
  job_id: string;
  job_name: string;
  order_id: string;
  order_name: string;
  driver_name: string | null;
  customer_name: string;
  email: string | null;
  phone: string | null;
  address: string;
  delivery_notes: string | null;
  operator_note: string | null;
  job_status: string;
  scale: number;
  /** All line items, in the order the backend sorted them. */
  items: PackagingItem[];
  bag_types: Array<{ type: string; quantity: number }>;
}

export interface PackagingGroup {
  key: string;
  /** Null for the `unassigned` sentinel bucket — frontend maps it
   *  to a localized string. Named driver buckets carry the
   *  resolved data label. */
  label: string | null;
  customers: PackagingCustomerEntry[];
}

export interface PackagingSummary {
  total_customers: number;
  total_items: number;
}

export interface PackagingResult {
  /**
   * Null when the backend built the list from an explicit job-id set, which
   * may span delivery dates. This hook only ever asks by date, so it always
   * sees a string in practice — the type stays honest about the contract
   * rather than about this one caller.
   */
  date: string | null;
  generated_at: string;
  groups: PackagingGroup[];
  summary: PackagingSummary;
}

/** Fetch the printable sheet data for an explicit set of delivery jobs. */
export async function fetchPackagingByJobIds(jobIds: readonly string[]): Promise<PackagingResult> {
  // The api-client unwraps the `{ data: PackagingResult }` envelope.
  return http.get<PackagingResult>('/api/packaging', {
    params: { ids: jobIds.join(','), format: 'json' },
  });
}

/** The whole delivery date's printout data, every live status, grouped by driver. */
export async function fetchPackagingByDate(date: string): Promise<PackagingResult> {
  return http.get<PackagingResult>('/api/packaging', { params: { date, format: 'json' } });
}
